import { createQueryKeys, type Query } from '@shared/api';

/**
 * Ключи журнала учёта путевых листов (ADR 0037).
 *
 * Корень гасят два десятка мест, и почти все они снаружи журнала. Причина в том, что своей
 * двери «выписать» у журнала нет вовсе: лист рождается там, где собрано печатаемое, — выпиской с
 * рейса, недельной сверкой ЭСМ-2 и выпиской по требованию (разобрано у ручек, в
 * `api/waybillsApi.ts`), а списывается коррекцией рейса, аннулированием и досрочным завершением.
 * Значит бумага меняется от команд, которые собственных экранов журнала не касаются, и гасить его
 * обязан каждый, кто эту бумагу тронул.
 *
 * Ключ здесь один, и пришёл он сюда не сразу: пока у журнала не было своего слайса, такой же пустой
 * `waybillKeys` стоял в `entities/vehicle-request` — там жили ручки листов. Двойственность прожила
 * ровно одну волну и снята вместе с переводом двадцати потребителей; помнить о ней стоит одно:
 * ячейку кэша задаёт кортеж, а не модуль, из которого он пришёл, поэтому два таких ключа были
 * безопасны, а односторонняя правка одного из них — нет. Развести корень в одном месте значило бы
 * молча разделить ячейку надвое, и журнал перестал бы обновляться после выписки, ничем этого не
 * показав.
 *
 * Свести их реэкспортом было нельзя: `entities` не импортирует `entities`, а страницы стоят выше
 * обоих слайсов, и два импорта вместо одного им ничего не стоили.
 *
 * Формы ключей повторяют то, что экраны собирали литералами, сегмент в сегмент, — и теперь экраны
 * зовут эти семейства. Совпадение было не педантизмом переноса: сегмент — это ячейка кэша, и
 * лишний или переставленный не ломает ни типов, ни запроса — экран просто начинает читать пустоту.
 * Где форма выглядит случайной, это сказано у самого семейства, а не исправлено молча.
 */
export const waybillKeys = createQueryKeys('waybills', {
  /**
   * Журнал целиком: отбор страницы, как его собрал экран.
   *
   * Сегмента с именем семейства нет — ключ так и остаётся `['waybills', отбор]`. Это не забытая
   * `list`: сегмент сменил бы ячейку кэша единственному экрану журнала, ничего при этом не
   * починив, — корень `['waybills']` и сейчас накрывает список вместе с карточками.
   *
   * Отбор объектом целиком, а не полями врозь: новый фильтр журнала попадает в ключ сам, а
   * перечисленный руками список однажды забыли бы дополнить — и журнал показал бы прежние строки
   * под новым фильтром. Период приходит уже приведённым к датам бланка: тем же видом он уходит и в
   * запрос, и ключ, посчитанный до приведения, отдал бы одному из двух периодов чужие строки.
   */
  list: (params: Query) => [params],
  /**
   * Один лист по идентификатору: отметки печати и выгрузки (Р18) и подшитые к номеру файлы (Р34).
   * Спрашивает его окно коррекции рейса — и спрашивает именно у журнала, а не считает заново:
   * «уходила ли бумага» — вопрос журнала, и два разных ответа на него хуже одного.
   *
   * Форма позиционно совпадает со списком, и различает их только тип аргумента — строка против
   * объекта отбора. Ячейки от этого разные (TanStack Query сличает значения, а не буквы), но
   * читающему ключ глазами `['waybills', X]` не говорит, карточка это или страница журнала.
   * Оставлено как есть: сегмент — это ячейка кэша.
   *
   * `undefined` допустим: листа у рейса может и не быть, а окно живёт до ответа предпросмотра —
   * запрос тогда выключен (`enabled`), но ключ собирается всё равно, и подставлять вместо
   * `undefined` пустую строку нельзя: это была бы другая ячейка кэша у окна, которое ничего не
   * спрашивало.
   */
  detail: (id: string | undefined) => [id],
});
